/**
 * Pure helpers for the P17 Cash on Delivery admin UI (P17-T11 step 1).
 *
 * Two responsibilities:
 *
 *   1. `orderStatusTone(s)` — extends the V1 status-tone mapper with
 *      `out_for_delivery → 'info'`. Mirrors the existing OrderDetail.tsx
 *      `STATUS_TONE` map (`paid → olive`, `cancelled → danger`, etc.) so
 *      the new status renders the same neutral-blue treatment as `placed`
 *      while the cash is in transit.
 *
 *   2. State-machine predicates — `isCodOrder`, `canDispatch`,
 *      `canMarkCollected`, `canMarkRefused` — encode the spec §6 state
 *      machine for the admin action bar:
 *
 *        placed                + cod  → Dispatch
 *        out_for_delivery      + cod  → Mark cash collected / Mark refused
 *
 *      Pure functions; no DOM, no fetch. Per L26 (admin SPA test
 *      discipline) every helper is exhaustively unit-tested in the
 *      companion `cod-helpers.test.ts` — route components (OrderDetail
 *      etc.) get build + typecheck + happy-path coverage only.
 *
 * Type-shape note: the predicates accept a structural slice
 * `{ status; payment_processor }` rather than the full `AdminOrderDetailDTO`
 * so they can be exercised both from real DTO instances and from the
 * lightweight `{ status, payment_processor }` row shape we plan to use in
 * the OrderList "Driver" column rendering. Same convention as
 * `orderStatusTone` taking a bare `OrderStatus` enum value.
 */
import type { OrderStatus } from "@wmart/shared-types";
import type { Tone } from "@wmart/ui";

/**
 * `payment_processor` slice surfaced on every order DTO. Modelled here as
 * `string | null` (rather than the wire-DTO's narrow union) so callers
 * can pass `AdminOrderSummaryDTO` / `AdminOrderDetailDTO` / raw row
 * shapes without re-narrowing — the predicates only care whether the
 * literal value === `"cod"`.
 */
export interface CodOrderLike {
  status: OrderStatus;
  payment_processor: string | null;
}

/**
 * Admin-SPA status-tone map, extended for the P17 COD lifecycle.
 *
 * Tones follow the brief carried into OrderDetail.tsx / Dashboard.tsx:
 *   - `paid → olive` (in-flight; cash settled, not yet shipped)
 *   - `fulfilled → success` (done)
 *   - `cancelled → danger`, `partially_refunded → warning`,
 *     `refunded → neutral` (terminal / problem states)
 *   - `placed → info`, `pending_payment → warning`,
 *     `out_for_delivery → info` (interstitial / action needed)
 *
 * Returns the `Tone` union from `@wmart/ui`. The Dashboard maps onto a
 * narrower 5-tone subset; this helper covers the full StatusPill Tone
 * surface so it can drive every admin status badge.
 */
export function orderStatusTone(s: OrderStatus): Tone {
  switch (s) {
    case "pending_payment":
      return "warning";
    case "placed":
      return "info";
    case "out_for_delivery":
      return "info";
    case "paid":
      return "olive";
    case "fulfilled":
      return "success";
    case "cancelled":
      return "danger";
    case "partially_refunded":
      return "warning";
    case "refunded":
      return "neutral";
  }
}

/**
 * Is this order on the COD payment path? True iff `payment_processor==='cod'`.
 *
 * The DB CHECK constraint `orders_out_for_delivery_cod_only` guarantees
 * status='out_for_delivery' IMPLIES payment_processor='cod', so callers
 * that already know the status is `out_for_delivery` may skip this guard
 * — we keep the explicit check anyway for symmetry with `canDispatch`
 * (which has no DB equivalent for `placed + cod`).
 */
export function isCodOrder(o: Pick<CodOrderLike, "payment_processor">): boolean {
  return o.payment_processor === "cod";
}

/**
 * Spec §6 transition `placed → out_for_delivery` (COD only). Drives the
 * "Dispatch for delivery" primary button in the OrderDetail action bar.
 */
export function canDispatch(o: CodOrderLike): boolean {
  return o.status === "placed" && isCodOrder(o);
}

/**
 * Spec §6 transition `out_for_delivery → paid` (COD only). Drives the
 * "Mark cash collected" primary (green) button in the OrderDetail
 * action bar.
 */
export function canMarkCollected(o: CodOrderLike): boolean {
  return o.status === "out_for_delivery" && isCodOrder(o);
}

/**
 * Spec §6 transition `out_for_delivery → cancelled` (COD only). Drives
 * the "Mark COD refused" secondary (orange) button in the OrderDetail
 * action bar. Same guard as `canMarkCollected` since both transitions
 * originate from the same source state.
 */
export function canMarkRefused(o: CodOrderLike): boolean {
  return o.status === "out_for_delivery" && isCodOrder(o);
}
